import {noop} from '@benchling/js-utils';
import type {Dispatch, MutableRefObject, SetStateAction} from 'react';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {ReactFlowProvider} from 'reactflow';

import {ProcessSingletonTaskSchemaName} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FlowchartEditorMode,
  isExecutionEditorMode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  FlowchartEditorLoadError,
  FlowchartTaskOrSchemaInaccessibleError,
} from 'processes/modals/ProcessFlowchartModalShared';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import FlowchartSidepanelManager from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelManager';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import ProcessFlowchartConfigEditorContent from '../ProcessFlowchartConfigEditorContent';
import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from '../redux/reduceFlowchartConfig';
import {SingletonTaskSchemasContext} from '../SingletonTaskSchemasContext';
import TransitionsManager from '../types/transition/TransitionsManager';
import {
  FlowchartConfigWithPartialMetadata,
  LiveStateChanges,
} from '../useFlowchartState/BaseFlowchartConfigStateManager';
import {
  validateFlowchartData,
  validateFlowchartDataForExecution,
} from '../useFlowchartValidationAndAutosaverControl';
import {
  ExecutionFlowchartRuntimeContext,
  useExecutionFlowchartRuntime,
} from './ExecutionFlowchartRuntimeContext';
import {
  ExecutionFlowchartPanePhase,
  type ExecutionFlowchartPaneReadiness,
} from './useExecutionFlowchartPaneReadiness';

const NOOP_LIVE_STATE_CHANGES: LiveStateChanges = {
  dirtyState: [false, noop],
  readonlyState: [false, noop],
  isSaveInFlightState: [false, noop],
  isAutosaverPausedState: [false, noop],
  isAutosaverBlockedState: [false, noop],
};

/** Merges a partial draft (static flowchart and/or metadata patch) into the current draft. */
function applyPartialUpdate(
  current: FlowchartConfigWithMetadata,
  partial: FlowchartConfigWithPartialMetadata
): FlowchartConfigWithMetadata {
  const updatedStaticFlowchart = partial.staticFlowchart ?? current.staticFlowchart;
  let updatedMetadata = current.metadata;
  if (partial.flowchartConfigMetadata != null) {
    const definedEntries = Object.entries(partial.flowchartConfigMetadata).filter(
      ([, value]) => value !== undefined
    );
    updatedMetadata = {
      ...current.metadata,
      ...Object.fromEntries(definedEntries),
    };
  }
  return {staticFlowchart: updatedStaticFlowchart, metadata: updatedMetadata};
}

/**
 * Applies a partial draft update using `draftRef` as the source of truth for the current draft
 * so sequential calls in one tick see prior updates before React re-renders.
 */
function commitPartialDraftUpdate(
  draftRef: MutableRefObject<FlowchartConfigWithMetadata | null>,
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>,
  updater: (current: FlowchartConfigWithMetadata) => FlowchartConfigWithPartialMetadata | null
): FlowchartConfigMetadata | null {
  const current = draftRef.current;
  if (current == null) {
    return null;
  }
  const partial = updater(current);
  if (partial == null) {
    return null;
  }
  const next = applyPartialUpdate(current, partial);
  draftRef.current = next;
  setDraftState(next);
  return next.metadata;
}

function ExecutionFlowchartEditorContent(): JSX.Element {
  const {
    editorMode,
    data,
    executionEditorConfigurator,
    draftState: draftFlowchartConfigAndMetadata,
    setDraftState,
    onBlockingErrorsChange,
    allSchemasById,
    taskDefinitionsResult,
    singletonTaskSchemasByName: loadedSingletonSchemas,
  } = useExecutionFlowchartRuntime();

  const draftRef = useRef<FlowchartConfigWithMetadata | null>(draftFlowchartConfigAndMetadata);
  draftRef.current = draftFlowchartConfigAndMetadata;

  const singletonTaskSchemasByName = useMemo(
    () =>
      loadedSingletonSchemas ??
      ({} as Record<ProcessSingletonTaskSchemaName, ProcessTaskSchemaWithoutFlowchartConfigGraphQL>),
    [loadedSingletonSchemas]
  );

  const taskDefinitionsInFlowchart = useMemo(() => {
    if (taskDefinitionsResult == null) {
      return null;
    }
    const flowchartTaskSchemaIds = new Set([
      data.rootTaskSchema.id,
      ...data.availableTaskSchemas.map((schema) => schema.id),
      // Include schemas for old/non-latest PTDVs referenced by schema mapper configs (optional
      // transitions targeting a PTDV that is not the latest version of any task definition).
      ...Object.values(data.schemaMapperPtdvIdToTaskSchemaId ?? {}),
    ]);
    return taskDefinitionsResult.filter((def) =>
      flowchartTaskSchemaIds.has(def.latestVersion.processTaskSchema.id)
    );
  }, [
    taskDefinitionsResult,
    data.rootTaskSchema.id,
    data.availableTaskSchemas,
    data.schemaMapperPtdvIdToTaskSchemaId,
  ]);

  const getNodeByIdRef = useRef<(nodeId: string) => FlowchartNode | null>(() => null);
  const getNodeNameById = useCallback(
    (nodeId: string): string | null => getNodeByIdRef.current(nodeId)?.data.nodeName ?? null,
    []
  );

  const setDraftFlowchartConfigAndMetadata = useCallback(
    (
      updater: (current: FlowchartConfigWithMetadata) => FlowchartConfigWithPartialMetadata | null,
      _saveImmediately?: boolean,
      _skipSettingDirtyState?: boolean
    ): Promise<FlowchartConfigMetadata | null> =>
      Promise.resolve(commitPartialDraftUpdate(draftRef, setDraftState, updater)),
    [setDraftState]
  );

  const plugin = PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(
    executionEditorConfigurator.flowchartTypeName
  );

  const validateFlowchart = isExecutionEditorMode(editorMode)
    ? validateFlowchartDataForExecution
    : validateFlowchartData;

  const validationErrors: Array<ProcessFlowchartValidationError> = useMemo(
    () =>
      validateFlowchart(
        draftFlowchartConfigAndMetadata.staticFlowchart,
        data.flowchartConfigId,
        draftFlowchartConfigAndMetadata.metadata,
        data.rootTaskSchema,
        false,
        plugin,
        undefined,
        editorMode
      ),
    [
      draftFlowchartConfigAndMetadata,
      data.flowchartConfigId,
      data.rootTaskSchema,
      editorMode,
      plugin,
      validateFlowchart,
    ]
  );

  const hasBlockingErrors = useMemo(
    () => validationErrors.some((error) => error.blocksPersistence),
    [validationErrors]
  );

  useEffect(() => {
    onBlockingErrorsChange?.(hasBlockingErrors);
  }, [hasBlockingErrors, onBlockingErrorsChange]);

  return (
    <ReactFlowProvider>
      <SingletonTaskSchemasContext.Provider value={singletonTaskSchemasByName}>
        <ReactFlowLockedContext.Provider value={false}>
          <FlowchartSidepanelManager
            getNodeNameById={getNodeNameById}
            flowchartType={executionEditorConfigurator.flowchartTypeName}
          >
            <TransitionsManager draftFlowchartConfigAndMetadata={draftFlowchartConfigAndMetadata}>
              <ProcessFlowchartConfigEditorContent
                getNodeByIdRef={getNodeByIdRef}
                flowchart={draftFlowchartConfigAndMetadata.staticFlowchart}
                flowchartEditorConfigurator={executionEditorConfigurator}
                editorMode={editorMode}
                allSchemasById={allSchemasById}
                rootTaskSchema={data.rootTaskSchema}
                flowchartTaskDefinitionId={data.flowchartTaskDefinitionId}
                flowchartConfigId={data.flowchartConfigId}
                taskDefinitionsResult={taskDefinitionsInFlowchart}
                didInitialConfigHaveConditionalMapping={false}
                didInitialFlowchartHaveChainedRouters={data.hasChainedRouters}
                liveStateChanges={NOOP_LIVE_STATE_CHANGES}
                locked={false}
                validationErrors={validationErrors}
                draftFlowchartConfigAndMetadata={draftFlowchartConfigAndMetadata}
                setDraftFlowchartConfigAndMetadata={setDraftFlowchartConfigAndMetadata}
                initiallyExpandAddNodeTab={editorMode !== FlowchartEditorMode.EXECUTION_VIEWER}
                initialTransitions={data.initialTransitions}
                schemaMapperPtdvIdToTaskSchemaId={data.schemaMapperPtdvIdToTaskSchemaId ?? {}}
              />
            </TransitionsManager>
          </FlowchartSidepanelManager>
        </ReactFlowLockedContext.Provider>
      </SingletonTaskSchemasContext.Provider>
    </ReactFlowProvider>
  );
}

/**
 * Renders load/error/ready states for execution flowchart UIs; in READY, provides
 * {@link ExecutionFlowchartRuntimeContext} and embeds {@link ProcessFlowchartConfigEditorContent}.
 * Expects {@link ExecutionFlowchartPaneReadiness} from {@link useExecutionFlowchartPaneReadiness}.
 */
export default function ExecutionFlowchartEditor(props: {
  readiness: ExecutionFlowchartPaneReadiness;
}): JSX.Element {
  const {readiness} = props;
  if (readiness.phase === ExecutionFlowchartPanePhase.LOAD_ERROR) {
    return <FlowchartEditorLoadError />;
  }
  if (readiness.phase === ExecutionFlowchartPanePhase.INACCESSIBLE) {
    return <FlowchartTaskOrSchemaInaccessibleError />;
  }
  if (readiness.phase === ExecutionFlowchartPanePhase.LOADING) {
    return <CenteredHorizontalThrobber />;
  }
  return (
    <ExecutionFlowchartRuntimeContext.Provider value={readiness.value}>
      <ExecutionFlowchartEditorContent />
    </ExecutionFlowchartRuntimeContext.Provider>
  );
}
